import { css } from "@linaria/core";
import cx from "classnames";
import type { PointOptionsObject, SeriesLineOptions, SeriesScatterOptions } from "highcharts";
import React, { useCallback, useEffect, useMemo } from "react";
import type { Project } from "../../../../../../../../api/useProjects";
import t from "../../../../../../../../i18n";
import { locale } from "../../../../../../../../i18n/locale";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import { groupBy } from "diagnostics-lib/iterable";
import BlankStateV2 from "@ui/BlankStateV2";
import ButtonIcon from "@ui/ButtonIconV2";
import Highcharts from "@ui/Highcharts";
import { AlertLine, Link } from "@ui/IconV2/react";
import { DataProcessing } from "@ui/Illustration/react";
import Switch from "@ui/Switch";
import { colors, spacings } from "@ui/Theme";
import { DateTime } from "luxon";
import { ChartMenu } from "../../../../../../Chart/Menu";
import type { BlockPlotModelT } from "../../../../../schema/block/plot";
import { printableHidden } from "../../../../../utils/print";
import { blockHeader, blockHeaderActions } from "../_styles/BlockHeader";
import { AxisZoomTooltipHint } from "./AxisZoomTooltipHint";
import { ResetZoomButton } from "./ResetZoomButton";
import { TimeserieDisplayLegend } from "./TimeserieDisplayLegend";
import { useAnalysisLastCycle } from "./hook/useAnalysisLastCycle";
import { useCycleDisplay } from "./hook/useCycleDisplay";
import { usePlot } from "./hook/usePlot";
import { usePlotLink } from "./hook/usePlotLink";
import {
  usePlotPeriod,
  usePlotPeriodFromZoomEffect,
  usePlotPeriodSyncEffect,
} from "./hook/usePlotPeriod";
import type { Series } from "./utils/options";
import { ScatterContinuousLegend } from "./ScatterContinuousLegend";
import TooltipV2 from "@ui/TooltipV2";
import { BlockLoading } from "../Loading";
import { HIDE_ON_WIDGET_EXPORT } from "@ui/ExportWidgetButton";
import { PeriodSelector } from "../../PeriodSelector";
import { useBlockOptionUpdateCallback } from "../../Editor/PlotEditor/hook/useOptionUpdateCallback";
import { WidgetHeader } from "../WidgetHeader";
import { useDashboard } from "../../../../../hook/api/dashboard";
import { useAvailableResources } from "../../../../../hook/useAvailableResources";
import useGroups from "../../../../../../../../api/useGroups";
import { baseAggregationPeriodPresets } from "../../../../../../../../domain/aggregation/preset";

import { DashboardDatasetLoader } from "../../../../../component/DatasetLoader";
import type { Dataset } from "../../../../../hook/useDataset";

type BlockPlotProps = {
  project: Project;
  block: BlockPlotModelT;
  className?: string;
  allowFullscreen?: boolean;
  editable?: boolean;
};

export const BlockPlot = React.memo((props: BlockPlotProps) => (
  <DashboardDatasetLoader block={props.block} projectId={props.project.id}>
    {dataset => <BlockPlotContent {...props} dataset={dataset} />}
  </DashboardDatasetLoader>
));

const PlotError = {
  Data: {
    title: t("project.dashboard.block.plot.error.data.title"),
    subtitle: t("project.dashboard.block.plot.error.data.description"),
  },
  Config: {
    title: t("project.dashboard.block.plot.error.config.title"),
    subtitle: t("project.dashboard.block.plot.error.config.description"),
  },
};

const BlockPlotContent = React.memo(
  ({
    project,
    block,
    className,
    allowFullscreen = false,
    editable = false,
    dataset,
  }: BlockPlotProps & { dataset: Dataset }) => {
    const { dashboard } = useDashboard(project.id, block.dashboardId);
    const availableResources = useAvailableResources({ projectId: project.id });
    const plot = React.useRef<Highcharts.Chart>(null);
    const cycle = useAnalysisLastCycle(project.id);
    const [displayCycle, setDisplayCycle] = useCycleDisplay({
      block,
      chart: plot,
      scope: block.id,
    });

    const { options, formattedPeriodRange, preset } = usePlot(
      project,
      block,
      availableResources,
      dataset,
      cycle,
    );

    const { isLinked, setLink } = usePlotLink(block);

    const error = useMemo(() => {
      if (dataset.quantities.length === 0 && dataset.faults.length === 0) return PlotError.Data;
      if (options?.series?.length === 0) return PlotError.Data;

      // @ts-ignore
      if (options?.series?.every(s => s.data.length === 0)) return PlotError.Data;
      if (options === null) return PlotError.Config;
      return null;
    }, [dataset.faults.length, dataset.quantities.length, options]);

    const groups = useGroups(project.id);
    const presets = useMemo(
      () =>
        baseAggregationPeriodPresets({
          timezone: project.timezone,
          groups,
          limit: {
            from: new Date(dataset.periodRange.from),
            to: new Date(dataset.periodRange.to),
          },
        }),
      [project.timezone, dataset.periodRange.from, dataset.periodRange.to, groups],
    );

    const periodDefaultValue = useMemo(() => {
      const value = presets.find(
        preset => preset.metadata?.["id"] === block.config.options?.temporalZoom,
      );
      if (value) {
        return {
          start: typeof value.startDate === "function" ? value.startDate() : value.startDate,
          end: typeof value.endDate === "function" ? value.endDate() : value.endDate,
        };
      }
      return null;
    }, [presets, block]);

    const [selectedPeriod, setSelectedPeriod] = usePlotPeriod(block);

    const emptySerieNames = useMemo(
      () =>
        [
          ...groupBy(options?.series ?? [], "name")
            .mapValues(
              series =>
                (series as (SeriesLineOptions | SeriesScatterOptions)[]).filter(
                  s =>
                    s.data?.length === 0 ||
                    (s.custom?.["y"].identifier.length > 0 &&
                      s.custom?.["y"].column.length > 0 &&
                      (s.data as PointOptionsObject[])?.every(
                        ({ x, y }) =>
                          x === null || x === undefined || y === null || y === undefined,
                      )),
                ).length === series.length,
            )
            .entries(),
        ]
          .filter((serie): serie is NonNullable<[string, true]> => serie[1])
          .map(([name]) => name),
      [options?.series],
    );

    const updateBlockOption = useBlockOptionUpdateCallback();
    const toggleLinkEditable = useCallback(() => {
      if (editable) updateBlockOption("sync", !isLinked);
      setLink(!isLinked);
    }, [editable, isLinked, setLink, updateBlockOption]);

    // @fixme workaround for graph + stream layout issue
    const triggerResize = useDebouncedCallback(
      () => globalThis.dispatchEvent(new Event("resize")),
      [],
      500,
    );
    useEffect(() => void triggerResize(), [options, triggerResize]);

    /** @fixme */
    // Keep tracking of options:
    // Active analyses streaming currently refresh the state of the previous component lifecycle
    // don't match this new behavior. So we need to ensure the options exist before bind zoom events
    usePlotPeriodFromZoomEffect({ block, plot, options });
    usePlotPeriodSyncEffect({
      block,
      datasetPeriodRange: dataset.periodRange,
      plot,
      cycle,
    });

    return (
      <div className={cx(className)}>
        <header className={blockHeader}>
          <WidgetHeader
            block={block}
            projectName={project.name}
            dashboardTitle={dashboard?.title}
            formattedPeriodRange={formattedPeriodRange}
            editable={editable}
            allowFullscreen={allowFullscreen}
          />
          <div className={cx(blockHeaderActions, plotHeaderActions, HIDE_ON_WIDGET_EXPORT)}>
            <ResetZoomButton
              lastCycle={cycle}
              block={block}
              maxPeriod={dataset.periodRange}
              project={project}
              plot={plot}
            />
            <PeriodSelector
              project={project}
              defaultValue={periodDefaultValue}
              maxPeriod={dataset.periodRange}
              value={selectedPeriod ?? "ALL"}
              onChange={setSelectedPeriod}
            />

            <TooltipV2 overlay={t("tooltip.block.plot.link")}>
              <ButtonIcon
                onClick={toggleLinkEditable}
                checked={isLinked}
                className={printableHidden}
              >
                <Link size="m" />
              </ButtonIcon>
            </TooltipV2>
            {preset === "line" && (
              <ChartMenu
                marker={displayCycle}
                tooltipOverlay={t("tooltip.block.plot.display_settings")}
                className={printableHidden}
              >
                {/* <div style={{ padding: spacings.xs }}> */}
                <Switch checked={displayCycle} onChange={e => setDisplayCycle(e.target.checked)}>
                  {t("project.dashboard.editor.gui.section.display.cycle.title")}
                </Switch>
                {/* </div> */}
              </ChartMenu>
            )}
          </div>
        </header>
        {dataset.pending && dataset.quantities.length === 0 && dataset.faults.length === 0 && (
          <BlockLoading />
        )}
        {(dataset.quantities.length > 0 || dataset.faults.length > 0) && options && !error && (
          <>
            <Highcharts
              ref={plot}
              name=""
              selectablePoints
              normalizeNavigatorSeries
              options={options}
              className={blockPlot}
            />
            {preset === "line" && (
              <TimeserieDisplayLegend
                series={(options.series as Series[]).filter(series => series.type === "line")}
              />
            )}
            {block.config.layers.every(l => l.encoding.color?.column === "date") && (
              <ScatterContinuousLegend
                startLabel={DateTime.fromMillis(dataset.periodRange.from).toFormat("dd LLL yyyy", {
                  locale,
                })}
                midLabel={DateTime.fromMillis(dataset.periodRange.from)
                  .plus(
                    DateTime.fromMillis(dataset.periodRange.to)
                      .diff(DateTime.fromMillis(dataset.periodRange.from))
                      .toMillis() / 2,
                  )
                  .toFormat("dd LLL yyyy", { locale })}
                endLabel={DateTime.fromMillis(dataset.periodRange.to).toFormat("dd LLL yyyy", {
                  locale,
                })}
              />
            )}
            {block.config.layers.every(l => l.encoding.color?.column === "hour") && (
              <ScatterContinuousLegend startLabel="00:00" midLabel="12:00" endLabel="23:00" />
            )}
            {block.config.layers.every(l => l.encoding.color?.column === "dayOfYear") && (
              <ScatterContinuousLegend
                startLabel={DateTime.fromObject({ year: 2020, ordinal: 1 }).toFormat("dd LLL", {
                  locale,
                })}
                midLabel={DateTime.fromObject({ year: 2020, ordinal: 183 }).toFormat("dd LLL", {
                  locale,
                })}
                endLabel={DateTime.fromObject({ year: 2020, ordinal: 366 }).toFormat("dd LLL", {
                  locale,
                })}
              />
            )}
            <AxisZoomTooltipHint plot={plot} />
            {emptySerieNames.length > 0 && (
              <p className={emptySerieWarn}>
                <AlertLine size="l" color={colors.phosphorus400} />
                <span>{t("project.dashboard.block.plot.empty_series", emptySerieNames)}</span>
              </p>
            )}
          </>
        )}
        {!dataset.pending && error && (
          <BlankStateV2
            picto={<DataProcessing size="l" />}
            title={error.title}
            subtitle={error.subtitle}
          />
        )}
      </div>
    );
  },
);

const blockPlot = css`
  width: 100%;

  /** Highcharts custom style override */
  svg g[class~="highcharts-axis"],
  svg g[class~="highcharts-axis-labels"]:not(.highcharts-navigator-xaxis) {
    pointer-events: bounding-box;
    cursor: pointer;
  }

  svg g[class~="highcharts-axis-labels"] text {
    pointer-events: none;

    &[x="0"] {
      display: none;
    }
  }

  .highcharts-scrollbar,
  .highcharts-scrollbar-thumb,
  .highcharts-scrollbar-track,
  .highcharts-scrollbar-button {
    stroke-width: 0;
    stroke: unset;
    width: 12px;
  }

  .highcharts-scrollbar-thumb {
    fill: ${colors.titanium300};
    transition: fill 150ms ease-in-out;

    &:hover {
      fill: ${colors.titanium500};
    }
  }
  .highcharts-scrollbar-arrow {
    transform: translate(-0.5px, -0.5px);
    fill: ${colors.titanium500};
  }
  .highcharts-scrollbar-button {
    height: 12px;
    fill: ${colors.titanium100};
    clip-path: circle(50%);
  }
  .highcharts-scrollbar-rifles {
    opacity: 0;
  }
  .highcharts-scrollbar-track {
    fill: transparent;
  }

  .highcharts-legend-item text,
  .highcharts-axis-title {
    font-weight: 600;
  }
`;

const plotHeaderActions = css`
  display: flex;
  gap: 8px;
  align-items: center;
  align-self: flex-start;
  justify-content: flex-end;
`;

const emptySerieWarn = css`
  background-color: ${colors.phosphorus50};
  padding: ${spacings.m};
  color: ${colors.carbon800};
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-template-rows: min-content;
  align-items: center;
  gap: ${spacings.xs};
  margin-top: ${spacings.m};

  svg {
    align-self: start;
  }
`;
